'use client'

import { useState } from 'react'
import { fulfillmentApi, marketplaceLabels, type FulfillmentApi } from '@entities/fulfillment'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
} from '@ui/base/combobox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { typeClass } from '@utils/typography'

import { maxRequestWarehouseCount } from '../config'
import { useDirectorySearch } from '../lib/use-directory-search'
import { type WizardWarehouse } from '../model/types'

/** Подсказка справочника: числовой код есть только у складов Wildberries. */
type WarehouseOption = { key: string; id: number | null; name: string; marketplace: string }

const searchWildberries = (query: string): Promise<WarehouseOption[]> =>
  fulfillmentApi.warehouseOptions({ q: query, limit: 20 }).then((response) =>
    (response.data.data?.warehouses ?? []).map((warehouse) => ({
      key: `wildberries-${warehouse.id}`,
      id: warehouse.id,
      name: warehouse.name,
      marketplace: 'wildberries',
    }))
  )

const searchOzon = (query: string): Promise<WarehouseOption[]> =>
  fulfillmentApi.ozonWarehouseOptions({ q: query, limit: 20 }).then((response) =>
    (response.data.data?.warehouses ?? []).map((warehouse) => ({
      key: warehouse.id,
      id: null,
      name: warehouse.name,
      marketplace: 'ozon',
    }))
  )

/** Недоступность одной площадки не должна прятать подсказки другой, поэтому ошибки разбираем порознь. */
const searchWarehouses = async (query: string): Promise<WarehouseOption[]> => {
  const results = await Promise.allSettled([searchWildberries(query), searchOzon(query)])
  if (results.every((result) => result.status === 'rejected')) {
    throw new Error('warehouse directory is unavailable')
  }

  return results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
}

/**
 * Склады отгрузки: подсказки берём из справочников Wildberries и Ozon, склад любой другой
 * площадки продавец вписывает сам.
 */
export const WarehousePicker = ({
  warehouses,
  onChange,
}: {
  warehouses: WizardWarehouse[]
  onChange: (warehouses: WizardWarehouse[]) => void
}) => {
  const [query, setQuery] = useState('')
  const [isListOpen, setIsListOpen] = useState(false)
  const { options, isFailed } = useDirectorySearch(query, searchWarehouses)
  const isLimitReached = warehouses.length >= maxRequestWarehouseCount

  const addWarehouse = (warehouse: WizardWarehouse) => {
    // Предел держится не только на disabled у полей выше: любая новая точка добавления обошла бы
    // его молча, а перебор всплыл бы уже отказом бэкенда.
    if (isLimitReached) {
      return
    }
    const isDuplicate = warehouses.some(
      (selected) => selected.name.toLocaleLowerCase('ru') === warehouse.name.toLocaleLowerCase('ru')
    )
    if (!isDuplicate) {
      onChange([...warehouses, warehouse])
    }
    setQuery('')
    setIsListOpen(false)
  }

  return (
    <div className="grid gap-3">
      <Field data-invalid={isFailed}>
        <FieldLabel htmlFor="request-warehouse-search">Склад отгрузки</FieldLabel>
        <Combobox
          items={options}
          filter={null}
          value={null}
          onValueChange={(option: WarehouseOption | null) => {
            if (option) {
              addWarehouse({ id: option.id, name: option.name })
            }
          }}
          inputValue={query}
          onInputValueChange={(value, details) => {
            if (details.reason !== 'input-change') {
              return
            }
            setQuery(value)
            setIsListOpen(Boolean(value.trim()))
          }}
          itemToStringLabel={(option: WarehouseOption) => option.name}
          open={isListOpen && options.length > 0}
          onOpenChange={setIsListOpen}
          openOnInputClick={false}
        >
          <ComboboxInput id="request-warehouse-search" placeholder="Например, Коледино" disabled={isLimitReached} />
          <ComboboxPositioner>
            <ComboboxPopup>
              <ComboboxEmpty>Склад не найден</ComboboxEmpty>
              <ComboboxList>
                {options.map((option) => (
                  <ComboboxItem key={option.key} value={option}>
                    <ComboboxItemIndicator />
                    <span className="flex flex-wrap items-baseline gap-2">
                      {option.name}
                      <span className={`${typeClass.caption} text-muted-foreground`}>
                        {marketplaceLabels[option.marketplace as FulfillmentApi.Marketplace] ?? option.marketplace}
                      </span>
                    </span>
                  </ComboboxItem>
                ))}
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </Combobox>
        <FieldError>{isFailed ? 'Справочник складов недоступен. Впишите название склада сами.' : ''}</FieldError>
      </Field>

      <Button
        type="button"
        variant="outline"
        disabled={isLimitReached || !query.trim()}
        onClick={() => addWarehouse({ id: null, name: query.trim() })}
      >
        Добавить склад по названию
      </Button>

      {warehouses.length === 0 && (
        <span className={`${typeClass.supporting} text-muted-foreground`}>
          Склад не выбран — фулфилменты предложат свои направления.
        </span>
      )}
      {isLimitReached && (
        <span className={`${typeClass.supporting} text-muted-foreground`}>
          Больше {maxRequestWarehouseCount} складов в одну заявку не поместится. Чтобы добавить другой, сначала уберите
          ненужные.
        </span>
      )}
      {warehouses.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Выбранные склады">
          {warehouses.map((warehouse) => (
            <div key={warehouse.name} className="flex items-center gap-1">
              <Badge variant="secondary">{warehouse.name}</Badge>
              <Button
                type="button"
                aria-label={`Убрать ${warehouse.name}`}
                variant="ghost"
                size="icon-sm"
                onClick={() => onChange(warehouses.filter((item) => item.name !== warehouse.name))}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
